import {useSearchParams} from "react-router-dom";
import {useEffect, useMemo, useState} from "react";
import type {DataLoader, DataRequestParams, DataRequestState, Filter, Sort} from "./types";
import type {Identifiable} from "../../api";
import {QueryKey, useQuery} from "react-query";
import {GridFilterModel, GridLocaleText, GridPaginationModel, GridSortModel} from "@mui/x-data-grid";
import {useTranslation} from "react-i18next";
import {deDE, enUS} from "@mui/x-data-grid/locales";

interface UseCustomDatatableProps<TElement extends Identifiable, TFilter extends Filter> {
    queryKey: QueryKey;
    loadData: DataLoader<TFilter, TElement>;
    tableName: string;
    defaultPageSize: number;
    request: DataRequestState<TFilter>;
    setFiltered: (filtered: string) => void;
}

function useCustomDatatable<
    TElement extends Identifiable,
    TFilter extends Filter
>({
      queryKey,
      loadData,
      tableName,
      defaultPageSize,
      request: {params, sort, setPagination, setSort},
      setFiltered,
  }: UseCustomDatatableProps<TElement, TFilter>
) {
    const [searchParams, setSearchParams] = useSearchParams();

    const paramKeyPage: string = `${tableName}Page`;
    const paramKeyPageSize: string = `${tableName}PageSize`;
    const paramKeyDtSort: string = `${tableName}DatatableSort`;
    const paramKeyDtFilter: string = `${tableName}DatatableFilter`;

    const initialPage = parseInt(searchParams.get(paramKeyPage) ?? "0", 10);
    const initialLimit = parseInt(
        searchParams.get(paramKeyPageSize) ?? defaultPageSize.toString(),
        10
    );
    const initialSort = parseSort(searchParams.get(paramKeyDtSort));
    const initialFilter = parseFilter(searchParams.get(paramKeyDtFilter));

    const [currentPage, setCurrentPage] = useState<number>(initialPage);
    const [currentLimit, setCurrentLimit] = useState<number>(initialLimit);
    const [currentSort, setCurrentSort] = useState<Sort>(parseSort(searchParams.get(paramKeyDtSort)));
    const [currentFilter, setCurrentFilter] = useState<any[]>(parseFilter(searchParams.get(paramKeyDtFilter)));

    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: (parseFilter(searchParams.get(paramKeyDtFilter)) ?? []).map((f: any) => ({
            field: f.field,
            operator: f.operator ?? "contains",
            value: f.value,
        })),
    });

    useEffect(() => {
        setPagination(initialPage, initialLimit);
        setSort(initialSort);
        setFiltered(serializeFilter(initialFilter));
    }, []);

    const stableQueryKey = useMemo(
        () => [
            ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
            tableName,
            currentPage,
            currentLimit,
            currentSort,
            currentFilter,
        ],
        [queryKey, tableName, currentPage, currentLimit, currentSort, currentFilter]
    );

    const tableResults = useSearch({
        queryKey: stableQueryKey,
        loadData,
        params: {
            ...params,
            page: currentPage,
            limit: currentLimit,
            ...(currentSort.length > 0
                ? {
                    sort: currentSort
                        .map((s) => (s.direction === "desc" ? `-${s.field}` : s.field))
                        .join(","),
                }
                : {}),
            filter: serializeFilter(currentFilter),
        },
    });

    const handleSetPagination = (newPage: number, newLimit: number) => {
        setCurrentPage(newPage);
        setCurrentLimit(newLimit);
        setSearchParams({
            ...Object.fromEntries(searchParams.entries()),
            [paramKeyPage]: newPage.toString(),
            [paramKeyPageSize]: newLimit.toString(),
        });
        setPagination(newPage, newLimit);
    };

    const handlePaginationModelOnChange = (newModel: GridPaginationModel) => {
        if (tableResults.isLoading) {
            handleSetPagination(currentPage, currentLimit);
        } else {
            handleSetPagination(newModel.page, newModel.pageSize);
        }
    }

    const handlePaginationOnChange = (page: number) => {
        if (tableResults.isLoading) {
            handleSetPagination(currentPage - 1, currentLimit);
        } else {
            handleSetPagination(page - 1, currentLimit);
        }
    }

    const handleSortModelOnChange = (newModel: GridSortModel) => {
        const sort = fromSortModel(newModel);
        setCurrentSort(sort);
        setSort(sort);

        setSearchParams({
            ...Object.fromEntries(searchParams.entries()),
            [paramKeyDtSort]: serializeSort(sort),
        });
    }

    const handleFilterModelOnChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);

        const filter = newModel.items.map((item) => ({
            field: item.field,
            operator: item.operator,
            value: item.value,
        }));

        const serializedFilters = serializeFilter(filter);

        setFiltered(serializedFilters);
        setCurrentFilter(filter);

        setSearchParams({
            ...Object.fromEntries(searchParams.entries()),
            [paramKeyDtFilter]: serializedFilters,
        });
    }

    return {
        sort,
        currentPage,
        currentLimit,
        tableResults,
        filterModel,
        handlePaginationModelOnChange,
        handlePaginationOnChange,
        handleSortModelOnChange,
        handleFilterModelOnChange,
        toPaginationModel,
        toSortModel,
        useLocaleText,
    }
}

function parseSort(value: string | null): Sort {
    if (!value) {
        return []
    } else {
        return value.split(",").map((s) => {
            const [field, direction] = s.split(":");
            return {field, direction: (direction as "asc" | "desc") ?? "asc"};
        });
    }
}

function serializeSort(sort: Sort): string {
    return sort.map((s) => `${s.field}:${s.direction}`).join(",");
}

function toSortModel(sort?: Sort): GridSortModel {
    return (sort ?? []).map(({field, direction}) => ({
        field,
        sort: direction,
    }));
}

function fromSortModel(model: GridSortModel): Sort {
    return model.map(({field, sort}) => ({field, direction: sort ?? "asc"}));
}

function parseFilter(value: string | null): any[] {
    if (!value) return [];
    try {
        return JSON.parse(value);
    } catch {
        return [];
    }
}

function serializeFilter(filter: any[]): string {
    return JSON.stringify(filter ?? []);
}

function toPaginationModel(page: number = 0, limit: number = 25): GridPaginationModel {
    return {
        page: page,
        pageSize: limit
    };
}

function useLocaleText(): Partial<GridLocaleText> {
    const {
        i18n: {language},
    } = useTranslation();

    switch (language) {
        case "de":
            return deDE.components?.MuiDataGrid?.defaultProps?.localeText ?? {};
        default:
            return enUS.components?.MuiDataGrid?.defaultProps?.localeText ?? {};
    }
}

function useSearch<TElement extends Identifiable, TFilter extends Filter>({
                                                                              queryKey,
                                                                              loadData,
                                                                              params,
                                                                          }: {
    queryKey: QueryKey;
    loadData: DataLoader<TFilter, TElement>;
    params: DataRequestParams<TFilter>;
}) {
    return useQuery(
        Array.isArray(queryKey) ? [...queryKey, params] : [queryKey, params],
        () => loadData(params),
        {suspense: false}
    );
}

export default useCustomDatatable;